import { Game } from "../models/Game";
import { ClockTimes, GameClock, Seat } from "../models/GameClock";
import { GameRecord } from "../models/GameHistory";

export interface ClockState extends ClockTimes {
    // Undefined when no clock runs.
    runningSeat?: Seat;
}

export function getSeat(players: { player1Id: string, player2Id: string }, playerId: string): Seat | undefined {
    if (playerId === players.player1Id) return "player1";
    if (playerId === players.player2Id) return "player2";
    return undefined;
}

// Both players' time at `now` (server time); never below zero.
export function getClockTimes(clock: GameClock, runningSeat: Seat | undefined, now: number): ClockTimes {
    const elapsed = runningSeat && clock.turnStartedAt !== undefined ? Math.max(0, now - clock.turnStartedAt) : 0;
    return {
        player1Ms: Math.max(0, clock.player1Ms - (runningSeat === "player1" ? elapsed : 0)),
        player2Ms: Math.max(0, clock.player2Ms - (runningSeat === "player2" ? elapsed : 0)),
    };
}

// "4:59"; rounded up, so "0:00" only shows when the time is gone.
export function formatClock(milliseconds: number): string {
    const seconds = Math.ceil(Math.max(0, milliseconds) / 1000);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

type ClockRecord = Pick<GameRecord, "clockInitialMs" | "moves" | "player1" | "result" | "endReason">;

// Both players' time after the given move of a saved game; move 0 is the start. Undefined for games saved before
// the game clock.
export function getClockAtMove(record: ClockRecord, moveNumber: number): ClockTimes | undefined {
    if (record.clockInitialMs === undefined) return undefined;
    const n = Math.min(Math.max(moveNumber, 0), record.moves.length);
    const times: ClockTimes = { player1Ms: record.clockInitialMs, player2Ms: record.clockInitialMs };
    for (const move of record.moves.slice(0, n)) {
        if (move.remainingMs === undefined) continue;
        if (move.playerId === record.player1.userId) times.player1Ms = move.remainingMs;
        else times.player2Ms = move.remainingMs;
    }
    // The player who ran out of time did not get to move again; their clock ends at zero.
    if (n === record.moves.length && record.endReason === "timeout" && record.result !== null) {
        if (record.result === "player1") times.player2Ms = 0;
        if (record.result === "player2") times.player1Ms = 0;
    }
    return times;
}

// Follows the clock of a live game from the server's messages and counts down between them, in server time.
export class ClockSync {
    private clock?: GameClock;
    private runningSeat?: Seat;
    // Server time minus local time.
    private offsetMs = 0;

    // From a live game message (game start, update, reconnect).
    update(game: Game, receivedAt = Date.now()) {
        const { mancalaGame } = game;
        this.clock = game.clock;
        this.runningSeat = mancalaGame.state === "ended" ? undefined : getSeat(mancalaGame, mancalaGame.turnPlayerId);
        if (game.serverTime !== undefined) this.offsetMs = game.serverTime - receivedAt;
    }

    // When the game has ended; the server sends the stopped clocks.
    stop(clock?: GameClock) {
        if (clock) this.clock = clock;
        this.runningSeat = undefined;
    }

    get isRunning(): boolean {
        return this.clock?.turnStartedAt !== undefined && this.runningSeat !== undefined;
    }

    // Undefined when the server sends no clock.
    getTimes(now = Date.now()): ClockState | undefined {
        if (!this.clock) return undefined;
        const runningSeat = this.isRunning ? this.runningSeat : undefined;
        return { ...getClockTimes(this.clock, runningSeat, now + this.offsetMs), runningSeat };
    }
}
